import React, { useState } from "react";
import { currencyFormat } from "simple-currency-format";
import {
  Table,
  Text,
  ActionIcon,
  Box,
  Flex,
  Image,
  Badge,
  Tooltip,
} from "@mantine/core";
import { HiChevronUp, HiChevronDown, HiReply } from "react-icons/hi";
import dayjs from "dayjs";
import { useAuthStore } from "../../store/useAuthStore";
import { useRecordStore } from "../../store/useRecordStore";

import Banregio from "../../assets/banks/banregio.webp";
import Azteca from "../../assets/banks/azteca.webp";
import Bbva from "../../assets/banks/bbva2.png";
import Hsbc from "../../assets/banks/hsbc.png";
import MercadoPago from "../../assets/banks/mercado-pago.webp";
import Santander from "../../assets/banks/santander-logo-1.webp";
import Efectivo from "../../assets/banks/efectivo.png";
import Spin from "../../assets/banks/spin.png";

const bankImages = {
  "Mercado Pago": MercadoPago,
  BBVA: Bbva,
  Santander: Santander,
  HSBC: Hsbc,
  Banregio: Banregio,
  Azteca: Azteca,
  Efectivo: Efectivo,
  Spin: Spin,
};

const RecordRow = ({ item }) => {
  const [show, setShow] = useState(false);
  const toggleDetails = () => setShow((prev) => !prev);
  const { role } = useAuthStore();
  const { toggleRollback } = useRecordStore();

  const isAdmin = role === 'ADMIN' || role === 'SUPER_ADMIN';
  const isCurrentMonth = dayjs(item.created_date).isSame(dayjs(), 'month');
  // Restrict revert to admins AND current month only — past records are considered final to prevent accidental reversals.
  const canRevert = isAdmin && isCurrentMonth;

  return (
    <>
      <Table.Tr 
        className={`transition-colors duration-200 ${show ? 'bg-slate-50' : 'hover:bg-slate-50/50'}`}
        style={show ? { borderLeft: '4px solid var(--mantine-color-yellow-filled)' } : { borderLeft: '4px solid transparent' }}
      >
        <Table.Td px="md" py="md">
          <Flex align="center" gap="sm">
            <Box className="w-8 h-8 flex items-center justify-center">
              <Image
                src={bankImages[item.type_bank_name]}
                alt={item.type_bank_name}
                fit="contain"
              />
            </Box>
            <Text size="xs" fw={700} c="dimmed" className="uppercase">{item.type_bank_name}</Text>
          </Flex>
        </Table.Td>
        
        <Table.Td>
          <Text size="sm" fw={600}>{dayjs(item?.created_date).format("DD/MMM/YYYY").toUpperCase()}</Text>
        </Table.Td>

        <Table.Td>
          <Badge variant="dot" color="gray" size="sm" radius="xs">
            {item.num_account}
          </Badge>
        </Table.Td>

        <Table.Td>
          <Text size="sm" className="font-mono" fw={700} c="dark.4">
            {currencyFormat(item.amount, "es-MX", "MXN", 2)}
          </Text>
        </Table.Td>

        <Table.Td align="right">
          <Flex gap="xs" justify="flex-end">
            {canRevert && (
              <Tooltip label="Revertir operación" fontSize="xs">
                <ActionIcon
                  variant="light"
                  color="red"
                  radius="md"
                  size="lg"
                  onClick={() => toggleRollback(item)}
                >
                  <HiReply />
                </ActionIcon>
              </Tooltip>
            )}
            <Tooltip label={show ? 'Cerrar detalles' : 'Ver más info'} fontSize="xs">
              <ActionIcon
                variant={show ? "filled" : "light"}
                color="yellow"
                radius="md"
                size="lg"
                onClick={toggleDetails}
              >
                {show ? <HiChevronUp /> : <HiChevronDown />}
              </ActionIcon>
            </Tooltip>
          </Flex>
        </Table.Td>
      </Table.Tr>

      {show && (
        <Table.Tr style={{ borderLeft: '4px solid var(--mantine-color-yellow-filled)', backgroundColor: 'var(--mantine-color-slate-50)' }}>
          <Table.Td colSpan={5} px="xl" py="lg">
            <Box className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Box>
                <Text size="xs" fw={800} c="yellow.9" mb="md" className="tracking-widest">DETALLES DEL CLIENTE</Text>
                <Box className="grid grid-cols-2 gap-y-2">
                  <DetailItem label="Nombre:" value={item.full_name} />
                  <DetailItem label="Email:" value={item.email} />
                  <DetailItem label="Teléfono:" value={item.phone} />
                  <DetailItem label="Celular:" value={item.cellphone} />
                </Box>
              </Box>
              
              <Box>
                <Text size="xs" fw={800} c="yellow.9" mb="md" className="tracking-widest">INFO DE PAGO</Text>
                <Box className="grid gap-y-2">
                  <DetailItem label="Referencia:" value={item.payment_reference || 'N/A'} isBadge />
                  <DetailItem label="Contrato:" value={item.type_contract} />
                  <DetailItem label="Comentarios:" value={item.comments || 'Sin comentarios'} isFull />
                </Box>
              </Box>
            </Box>
          </Table.Td>
        </Table.Tr>
      )}
    </>
  );
};

const DetailItem = ({ label, value, isBadge = false, isFull = false }) => (
  <Box style={isFull ? { gridColumn: 'span 2' } : {}}>
    <Text size="xs" fw={700} c="dimmed" className="uppercase" mb={2}>{label}</Text>
    {isBadge ? (
      <Badge color="yellow" variant="filled" radius="sm">{value}</Badge>
    ) : (
      <Text size="sm" fw={600} c="dark.5">{value}</Text>
    )}
  </Box>
);

export default RecordRow;
